<template>
  <transition name="fade">
    <div v-if="showing" class="mtx-modal" @click="onClick" @keydown="onKeydown">
      <div
        ref="content"
        :style="{
          width: width || false,
          maxWidth: maxWidth || false
        }"
        class="mtx-modal__content px-2 py-2"
        tabindex="-1"
        role="dialog"
      >
        <MtxCard>
          <slot :hide="hide" />
        </MtxCard>
      </div>
    </div>
  </transition>
</template>

<script>
import KEYCODES from "../../data/keycodes"
import FOCUSABLE from "../../data/focusable"
const NAME = "mtx-modal"
/**
 * A modal dialogue that traps user focus
 */
export default {
  name: NAME,

  model: {
    prop: "showing",
    event: "change"
  },

  props: {
    /**
     * @model
     */
    showing: Boolean,
    persistent: Boolean,
    width: String,
    maxWidth: String,
    allowScroll: Boolean
  },

  methods: {
    show() {
      /**
       * @event show
       * @type { boolean }
       */
      this.$emit("show")
      this.$emit("change", true)
    },

    hide() {
      /**
       * @event hide
       * @type { boolean }
       */
      this.$emit("hide")
      this.$emit("change", false)
    },

    toggle() {
      const event = this.showing ? "hide" : "show"
      this.$emit(event, !this.showing)
      /**
       * @event change
       * @type { boolean }
       */
      this.$emit("change", !this.showing)
    },

    onClick(event) {
      if (event.target.classList.contains(`${NAME}`) && !this.persistent) {
        this.hide()
      }
    },

    onKeydown(event) {
      if (event.keyCode === KEYCODES.ESC) {
        this.hide()
      }

      if (event.keyCode === KEYCODES.TAB) {
        const content = this.$refs.content
        const focusable = Array.from(content.querySelectorAll(FOCUSABLE))
        const activeElement = document.activeElement

        if (focusable.length === 0) {
          return event.preventDefault()
        }

        if (this.showing && content && !content.contains(activeElement)) {
          focusable[0].focus()
          return event.preventDefault()
        }

        const focusedItemIndex = focusable.indexOf(activeElement)
        if (event.shiftKey && focusedItemIndex === 0) {
          focusable[focusable.length - 1].focus()
          return event.preventDefault()
        }

        if (!event.shiftKey && focusedItemIndex === focusable.length - 1) {
          focusable[0].focus()
          return event.preventDefault()
        }
      }
    }
  },

  watch: {
    showing: {
      handler: function(next, prev) {
        if (typeof window !== "undefined") {
          if (next && next != prev) {
            !this.allowScroll && document.body.style.setProperty("overflow", "hidden")
            this.$nextTick(() => {
              this.$refs.content.focus()
            })
          } else {
            !this.allowScroll && document.body.style.removeProperty("overflow")
          }
        }
      }
    }
  }
}
</script>

<style lang="scss">
.mtx-modal {
  display: flex;
  align-items: center;
  justify-content: center;
  position: fixed;
  z-index: 100;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background: rgba(0, 0, 0, 0.3);

  &__content {
    overflow: auto;
    max-width: 95vw;
    max-height: 80vh;
    outline: 0;
    transition: transform 0.3s;

    .fade-enter &,
    .fade-leave-active & {
      transform: translateY(20px);
    }
  }
}
</style>
